import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlow } from './state/flow';
import { setLanguage, trackPageView } from './lib/analytics';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import FormPhotoFlow from './pages/FormPhotoFlow';
import Loading from './pages/Loading';
import WelcomeShareFlow from './pages/WelcomeShareFlow';
import QuizStart from './pages/QuizStart';
import QuizQuestion from './pages/QuizQuestion';
import QuizLoading from './pages/QuizLoading';
import QuizResults from './pages/QuizResults';
import DebugCard from './pages/DebugCard';
import DebugQuiz from './pages/DebugQuiz';
import './styles/app.scss';

/** Redirects back to / if state requirements aren't met. */
function RequireFlow({
  needsForm = false,
  needsPhoto = false,
  children,
}: {
  needsForm?: boolean;
  needsPhoto?: boolean;
  children: ReactElement;
}) {
  const { hasFormData, hasPhoto } = useFlow();
  if (needsForm && !hasFormData) return <Navigate to="/" replace />;
  if (needsPhoto && !hasPhoto) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isHome = location.pathname === '/';
  const isDebug = location.pathname.startsWith('/debug');
  const showChrome = !isHome && !isDebug;

  // Pageview fires only on real route changes. Language is reported separately
  // as a GA4 user_property in the effect below, so a language switch does not
  // double-count pageviews.
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  // Reset scroll on every route change. On mobile the document scrolls, so a
  // tall form like /id-card otherwise leaves the user mid-page when they hit
  // /photo; on desktop `.app__main` is the scroll container, reset it too.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const main = document.querySelector<HTMLElement>('.app__main');
    if (main) main.scrollTop = 0;
  }, [location.pathname]);

  // Set the user's language as a GA4 user_property and keep <html lang> in
  // sync so the browser does not offer to translate a page that's already
  // presented in the user's chosen language.
  useEffect(() => {
    const lang = i18n.resolvedLanguage ?? 'en';
    setLanguage(lang);
    if (document.documentElement.lang !== lang) {
      document.documentElement.lang = lang;
    }
  }, [i18n.resolvedLanguage]);

  return (
    <div className={`app${isHome ? ' app--home' : ''}`}>
      {showChrome && <Header />}
      <main className="app__main">
        <Routes>
          <Route path="/" element={<Home />} />
          {/* /id-card and /photo share a stable layout (FormPhotoFlow) so the
              60/40 → 40/60 slide can run as a CSS transition on stable DOM,
              instead of fighting the view-transition framework. */}
          <Route path="/id-card" element={<FormPhotoFlow />} />
          <Route path="/photo" element={<FormPhotoFlow />} />
          <Route
            path="/loading"
            element={
              <RequireFlow needsForm needsPhoto>
                <Loading />
              </RequireFlow>
            }
          />
          {/* /welcome and /share share a stable layout (WelcomeShareFlow) so
              the headline + subtitle stay put across the navigation; the
              right pane crossfades between the card preview and the share
              tiles. The flow component handles its own form + photo guard. */}
          <Route path="/welcome" element={<WelcomeShareFlow />} />
          <Route path="/share" element={<WelcomeShareFlow />} />
          {/* Quiz half — English-only. Components self-guard (bounce to /class
              when the episode is unknown, the name is missing, or no result
              exists yet). */}
          <Route path="/class" element={<QuizStart />} />
          <Route path="/class/:episode" element={<QuizQuestion />} />
          <Route path="/class/:episode/grading" element={<QuizLoading />} />
          <Route path="/class/:episode/results" element={<QuizResults />} />
          {/* TEMPORARY — remove these routes (and the DebugCard/DebugQuiz pages) before launch. */}
          <Route path="/debug/card" element={<DebugCard />} />
          <Route path="/debug/quiz" element={<DebugQuiz />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {showChrome && <Footer />}
    </div>
  );
}
